import React, {useEffect, useState} from "react";
import {
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	CircularProgress,
	Fade,
	FormControlLabel,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {
	Add as AddIcon,
	ArrowBack as ArrowBackIcon,
	Close as CloseIcon,
	Layers as LayersIcon,
	Remove as RemoveIcon,
	Save as SaveIcon,
	Update as UpdateIcon,
} from "@material-ui/icons";
import {useHistory, useParams} from "react-router";
import API from "../../../network/dashboardAPI";
import useAlert from "../../../services/useAlert";
import ItemSelected from "../ProductsSelection/components/ItemSelected";

const PAGE_TYPE = "selection";

// Field / operator vocabulary (same as landing / partner page builder)
const fields = [
	{field: "Product SKU", options: ["IN", "NOT IN"]},
	{field: "Product ID", options: ["IN", "NOT IN"]},
	{field: "Category", options: ["IN", "NOT IN"]},
	{field: "Subcategory", options: ["IN", "NOT IN"]},
	{field: "Product Name", options: ["starts with", "contains"]},
	{field: "Product Description", options: ["starts with", "contains"]},
	{field: "Price", options: ["bigger than", "less than"]},
	{field: "Supplier", options: ["IN", "NOT IN"]},
	{field: "Brand", options: ["IN", "NOT IN"]},
	{field: "Country", options: ["IN", "NOT IN"]},
	{field: "Dietary", options: ["IN", "NOT IN"]},
	{field: "Discount", options: ["YES", "NO"]},
	{field: "New Arrival", options: ["YES", "NO"]},
	{field: "Trending", options: ["YES", "NO"]},
];

const isTypeSKU = (type) =>
	[
		"Product SKU",
		"Product ID",
		"Category",
		"Subcategory",
		"Supplier",
		"Brand",
		"Country",
		"Dietary",
	].includes(type);

const isTypeBoolean = (type) => ["Discount"].includes(type);

const emptyCondition = () => ({field: "", option: "", value: []});

const generateSlug = (title) =>
	title
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, "")
		.replace(/\s+/g, "-")
		.replace(/-+/g, "-");

// has a usable value for saving
const hasValue = (field, value) => {
	if (isTypeBoolean(field)) return true; // option (YES/NO) is the value
	if (isTypeSKU(field)) return Array.isArray(value) && value.length > 0;
	return String(value ?? "").trim() !== "";
};

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
		backgroundColor: "#f9fafc",
		minHeight: "100vh",
	},
	topBar: {height: 4, background: "linear-gradient(90deg, #8bc34a 0%, #689f38 100%)"},
	titleRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	card: {
		borderRadius: 12,
		padding: theme.spacing(3),
		marginBottom: theme.spacing(3),
		boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
	},
	conditionCard: {
		border: "1px solid #e2e8f0",
		borderRadius: 12,
		padding: theme.spacing(2.5),
		marginBottom: theme.spacing(1),
		backgroundColor: "#fff",
		boxShadow: "none",
	},
	groupHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: theme.spacing(2),
	},
	groupBadge: {
		display: "inline-flex",
		alignItems: "center",
		gap: theme.spacing(1),
		background: "linear-gradient(135deg, #9cc54b 0%, #689f38 100%)",
		color: "#fff",
		borderRadius: 20,
		padding: theme.spacing(0.5, 2),
		fontWeight: 600,
	},
	productCount: {
		backgroundColor: "rgba(255,255,255,0.25)",
		color: "#fff",
		fontWeight: 600,
		borderRadius: 12,
		padding: theme.spacing(0.25, 1.25),
		fontSize: 12,
	},
	conditionRow: {
		padding: theme.spacing(1, 0),
	},
	selectField: {
		backgroundColor: "#fff",
		borderRadius: 8,
	},
	orDivider: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		margin: theme.spacing(1.5, 0),
	},
	orText: {
		background: "#689f38",
		color: "#fff",
		borderRadius: 16,
		padding: theme.spacing(0.25, 2),
		fontWeight: 700,
		fontSize: 12,
		letterSpacing: 1,
	},
	addBtn: {color: "#689f38", fontWeight: 600, marginTop: theme.spacing(1)},
	createBtn: {
		background: "linear-gradient(135deg, #8bc34a 0%, #689f38 100%)",
		color: "#fff",
		fontWeight: 600,
		borderRadius: 8,
		padding: theme.spacing(1, 3),
	},
	footer: {display: "flex", justifyContent: "flex-end", gap: theme.spacing(1)},
	loadingBox: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: theme.spacing(6),
	},
}));

export default function SelectionForm() {
	const classes = useStyles();
	const history = useHistory();
	const alert = useAlert();
	const {id} = useParams();
	const isEdit = Boolean(id);

	const [name, setName] = useState("");
	const [active, setActive] = useState(true);
	const [blocks, setBlocks] = useState([{id: 1, conditions: [emptyCondition()]}]);
	const [counts, setCounts] = useState({});
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);

	const [loadedGroups, setLoadedGroups] = useState([]);

	useEffect(() => {
		if (isEdit) loadSelection(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const loadSelection = async (pageId) => {
		try {
			setLoading(true);
			const response = await API.adminGetProductsSelectionById(pageId);
			const res = await response.json();
			const page = res?.data?.data?.data?.[0];
			if (!res.success || !page) {
				alert.error("Error", res.message || "Selection not found");
				return;
			}
			const countArr = res?.data?.data?.count || [];
			const countMap = {};
			countArr.forEach((c) => {
				countMap[c.id] = c.count;
			});
			setCounts(countMap);

			setName(page.title || "");
			setActive(page.status === 1);

			const loaded = (page.selection_groups || []).map((g) => ({
				id: g.id,
				conditions:
					(g.conditions || []).length > 0
						? g.conditions.map((c) => ({
							id: c.id,
							field: c.field || "",
							option: c.option || "",
							value: c.value,
						}))
						: [emptyCondition()],
			}));
			setBlocks(loaded.length > 0 ? loaded : [{id: 1, conditions: [emptyCondition()]}]);
			setLoadedGroups(
				(page.selection_groups || []).map((g) => ({
					id: g.id,
					conditionIds: (g.conditions || []).map((c) => c.id).filter(Boolean),
				}))
			);
		} catch (err) {
			console.error("Error loading selection:", err);
			alert.error("Error", "Failed to load selection");
		} finally {
			setLoading(false);
		}
	};

	const nextGroupId = () =>
		blocks.reduce((max, g) => (Number(g.id) > max ? Number(g.id) : max), 0) + 1;

	const addBlock = () =>
		setBlocks((prev) => [...prev, {id: nextGroupId(), conditions: [emptyCondition()]}]);

	const removeBlock = (blockId) =>
		setBlocks((prev) => prev.filter((g) => g.id !== blockId));

	const addCondition = (blockId) =>
		setBlocks((prev) =>
			prev.map((g) =>
				g.id === blockId ? {...g, conditions: [...g.conditions, emptyCondition()]} : g
			)
		);

	const removeCondition = (blockId, index) =>
		setBlocks((prev) =>
			prev.map((g) =>
				g.id === blockId
					? {...g, conditions: g.conditions.filter((_, i) => i !== index)}
					: g
			)
		);

	const updateCondition = (blockId, index, key, value) =>
		setBlocks((prev) =>
			prev.map((g) =>
				g.id === blockId
					? {
						...g,
						conditions: g.conditions.map((c, i) => {
							if (i !== index) return c;
							const next = {...c, [key]: value};
							if (key === "field") {
								next.option = "";
								next.value = [];
							}
							return next;
						}),
					}
					: g
			)
		);

	// from ItemSelected: (blockId, conditionIndex, fieldType, { selectedList })
	const handleSelectChange = (blockId, conditionIndex, field, {selectedList}) =>
		setBlocks((prev) =>
			prev.map((g) =>
				g.id === blockId
					? {
						...g,
						conditions: g.conditions.map((c, i) =>
							i === conditionIndex
								? {
									...c,
									value: (selectedList || []).map((v) => ({value: v.value, id: v.id})),
								}
								: c
						),
					}
					: g
			)
		);

	const buildData = () =>
		blocks.map((g) => ({
			id: g.id,
			conditions: g.conditions
				.filter((c) => c.field && c.option && hasValue(c.field, c.value))
				.map((c) => ({
					...(c.id ? {id: c.id} : {}),
					field: c.field,
					option: c.option,
					value: isTypeBoolean(c.field) ? c.option : c.value,
				})),
		}));

	const buildModify = () => {
		const currentGroupIds = new Set(blocks.map((g) => g.id));
		const deletedGroups = loadedGroups
			.filter((lg) => !currentGroupIds.has(lg.id))
			.map((lg) => ({id: lg.id}));

		const currentConditionIds = new Set();
		blocks.forEach((g) =>
			g.conditions.forEach((c) => {
				if (c.id) currentConditionIds.add(c.id);
			})
		);
		const deletedConditions = [];
		loadedGroups.forEach((lg) => {
			if (!currentGroupIds.has(lg.id)) return;
			lg.conditionIds.forEach((cid) => {
				if (!currentConditionIds.has(cid)) deletedConditions.push({id: cid});
			});
		});

		return {
			groups: {delete: deletedGroups, update: [], add: []},
			product_selection: {delete: deletedConditions, update: [], add: []},
		};
	};

	const handleSubmit = async () => {
		if (!name.trim()) {
			alert.error("Validation", "Name is required");
			return;
		}
		const data = buildData();
		if (!data.some((g) => g.conditions.length > 0)) {
			alert.error("Validation", "Add at least one complete condition (field, operator and value)");
			return;
		}

		const body = {
			title: name.trim(),
			slug: generateSlug(name),
			page_type: PAGE_TYPE,
			status: active ? 1 : 0,
			description: "",
			show_on_menu: 1,
			data,
		};

		try {
			setSaving(true);
			let response;
			if (isEdit) {
				response = await API.adminUpdateProductsSelection(id, {...body, modify: buildModify()});
			} else {
				response = await API.adminSetupProductsSelection(body);
			}
			const res = await response.json();
			if (res.error || res.success === false) {
				alert.error("Error", res.message || "Failed to save selection");
				return;
			}
			alert.toast(`Selection ${isEdit ? "updated" : "created"} successfully`, "success", 2000);
			history.push("/admin/selections");
		} catch (err) {
			console.error("Error saving selection:", err);
			alert.error("Error", err.message || "Failed to save selection");
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className={classes.root}>
				<Box className={classes.loadingBox}>
					<CircularProgress style={{color: "#9cc54b"}}/>
				</Box>
			</div>
		);
	}

	return (
		<div className={classes.root}>
			<Paper className={classes.card} style={{padding: 0, overflow: "hidden"}}>
				<div className={classes.topBar}/>
				<Box style={{padding: 24}}>
					<div className={classes.titleRow}>
						<IconButton size="small" onClick={() => history.push("/admin/selections")}>
							<ArrowBackIcon/>
						</IconButton>
						<div>
							<Typography variant="h5" style={{fontWeight: 700}}>
								{isEdit ? "Edit Selection" : "New Selection"}
							</Typography>
							<Typography variant="body2" color="textSecondary">
								Define a reusable product condition preset. Use the page_id in Strapi CMS.
							</Typography>
						</div>
					</div>

					<Box style={{display: "flex", alignItems: "center", gap: 16}}>
						<TextField
							label="Name"
							required
							variant="outlined"
							size="small"
							fullWidth
							value={name}
							onChange={(e) => setName(e.target.value)}
						/>
						<FormControlLabel
							control={
								<Checkbox
									color="primary"
									checked={active}
									onChange={(e) => setActive(e.target.checked)}
								/>
							}
							label="Active"
							style={{whiteSpace: "nowrap"}}
						/>
					</Box>
				</Box>
			</Paper>

			<Paper className={classes.card}>
				<Typography variant="h6" style={{fontWeight: 600}}>
					Condition Groups
				</Typography>
				<Typography variant="body2" color="textSecondary" gutterBottom>
					Groups use <strong>OR</strong> logic. Conditions within a group use <strong>AND</strong> logic.
				</Typography>

				<Fade in={!loading}>
					<Box>
						{blocks.map((block, blockIndex) => (
							<Box key={block.id}>
								<Card className={classes.conditionCard}>
									<Box className={classes.groupHeader}>
										<span className={classes.groupBadge}>
											<LayersIcon fontSize="small"/>
											Group {blockIndex + 1}
											<span className={classes.productCount}>
												{counts[block.id] || 0} products
											</span>
										</span>
										{blocks.length > 1 && (
											<Tooltip title="Remove this group">
												<IconButton onClick={() => removeBlock(block.id)} style={{color: "#e53e3e"}}>
													<CloseIcon/>
												</IconButton>
											</Tooltip>
										)}
									</Box>

									{block.conditions.map((condition, conditionIndex) => {
										const usedFields = block.conditions
											.filter((_, idx) => idx !== conditionIndex)
											.map((cond) => cond.field);

										return (
											<Box key={conditionIndex} className={classes.conditionRow}>
												<Grid container spacing={2} alignItems="center">
													<Grid item xs={12} md={3}>
														<Select
															fullWidth
															displayEmpty
															variant="outlined"
															className={classes.selectField}
															value={condition.field}
															onChange={(e) =>
																updateCondition(block.id, conditionIndex, "field", e.target.value)
															}
														>
															<MenuItem value="" disabled>
																Select field
															</MenuItem>
															{fields.map((f, idx) => (
																<MenuItem
																	key={idx}
																	disabled={usedFields.includes(f.field) && f.field !== condition.field}
																	value={f.field}
																>
																	{f.field}
																</MenuItem>
															))}
														</Select>
													</Grid>

													<Grid item xs={12} md={2}>
														<Select
															fullWidth
															displayEmpty
															variant="outlined"
															className={classes.selectField}
															value={condition.option}
															disabled={!condition.field}
															onChange={(e) =>
																updateCondition(block.id, conditionIndex, "option", e.target.value)
															}
														>
															<MenuItem value="" disabled>
																Operator
															</MenuItem>
															{fields
																.find((f) => f.field === condition.field)
																?.options?.map((option, idx) => (
																	<MenuItem key={idx} value={option}>
																		{option}
																	</MenuItem>
																))}
														</Select>
													</Grid>

													<Grid item xs={12} md={6}>
														{isTypeSKU(condition.field) ? (
															<ItemSelected
																fieldType={condition.field}
																value={
																	Array.isArray(condition.value)
																		? condition.value.map((v) => ({
																			label: v.value,
																			value: v.value,
																			id: v.id,
																		}))
																		: []
																}
																onchange={handleSelectChange}
																onRemove={handleSelectChange}
																item={{block: block.id, conditionIndex}}
																placeholder={`Search ${condition.field}`}
															/>
														) : !isTypeBoolean(condition.field) ? (
															<TextField
																fullWidth
																variant="outlined"
																label={condition.field || "Value"}
																disabled={!condition.field}
																value={Array.isArray(condition.value) ? "" : condition.value}
																onChange={(e) =>
																	updateCondition(block.id, conditionIndex, "value", e.target.value)
																}
															/>
														) : (
															<Typography variant="body2" color="textSecondary">
																No value needed — uses the operator (YES / NO).
															</Typography>
														)}
													</Grid>

													<Grid item xs={12} md={1}>
														{block.conditions.length > 1 && (
															<Tooltip title="Remove condition">
																<IconButton
																	onClick={() => removeCondition(block.id, conditionIndex)}
																	style={{color: "#e53e3e"}}
																>
																	<RemoveIcon/>
																</IconButton>
															</Tooltip>
														)}
													</Grid>
												</Grid>
											</Box>
										);
									})}

									<Button
										startIcon={<AddIcon/>}
										onClick={() => addCondition(block.id)}
										className={classes.addBtn}
										size="small"
									>
										Add Condition
									</Button>
								</Card>

								{blockIndex < blocks.length - 1 && (
									<Box className={classes.orDivider}>
										<span className={classes.orText}>OR</span>
									</Box>
								)}
							</Box>
						))}

						<Box style={{textAlign: "center", marginTop: 16}}>
							<Button
								variant="contained"
								className={classes.createBtn}
								startIcon={<AddIcon/>}
								onClick={addBlock}
							>
								Add Group
							</Button>
						</Box>
					</Box>
				</Fade>
			</Paper>

			<Paper className={classes.card}>
				<Box className={classes.footer}>
					<Button onClick={() => history.push("/admin/selections")}>Cancel</Button>
					<Button
						variant="contained"
						className={classes.createBtn}
						startIcon={isEdit ? <UpdateIcon/> : <SaveIcon/>}
						onClick={handleSubmit}
						disabled={saving}
					>
						{saving ? "Saving..." : isEdit ? "Update Selection" : "Create Selection"}
					</Button>
				</Box>
			</Paper>
		</div>
	);
}
